import * as yup from 'yup';
import { V1RecurringRuleInterval } from '@fintronners/react-api/src';
import { getOneOfEnumSchema } from '@fintronners/react-api/src/utils/formUtils/schemas/commonSchemas';
import i18n from '@fintronners/react-language/src/Lang';
import { TransferType, DepositType } from './constants';
import type { TransferValidationContext } from './types';
import {
  validateWithdrawalAmount,
  validateDepositAmount,
} from './transferValidators';

export { TransferType, DepositType };

export const intervals = [
  V1RecurringRuleInterval.EveryWeek,
  V1RecurringRuleInterval.EveryTwoWeeks,
  V1RecurringRuleInterval.EveryMonth,
];

export type { TransferValidationContext };

export const transferSchema = yup
  .object({
    transferType: getOneOfEnumSchema(TransferType).default(TransferType.DEPOSIT),
    depositType: getOneOfEnumSchema(DepositType).default(DepositType.ONE_TIME),
    amount: yup
      .string()
      .default('0')
      .required(i18n.t('errors.required'))
      .test('amount-validation', i18n.t('errors.required'), function (value) {
        const ctx = this.options.context as TransferValidationContext;
        if (!ctx) return true;

        const { transferType, depositType } = this.parent;
        const parsedAmount = parseFloat(value);

        if (!value || isNaN(parsedAmount)) {
          return this.createError({ message: i18n.t('errors.required') });
        }

        if (parsedAmount === 0) {
          return this.createError({ message: i18n.t('errors.required') });
        }

        let errorMessage: string | null = null;

        if (transferType === TransferType.WITHDRAWAL) {
          errorMessage = validateWithdrawalAmount(parsedAmount, ctx);
        } else if (transferType === TransferType.DEPOSIT) {
          // Skip recurring deposit amount validation against current balance
          // because these are future transactions
          if (depositType !== DepositType.RECURRING_DEPOSIT) {
            errorMessage = validateDepositAmount(parsedAmount, ctx);
          }
        }

        if (errorMessage) {
          return this.createError({ message: errorMessage });
        }

        return true;
      }),
    interval: yup.mixed<V1RecurringRuleInterval>().when(['transferType', 'depositType'], {
      is: (transferType: TransferType, depositType: DepositType) =>
        transferType === TransferType.DEPOSIT && depositType === DepositType.RECURRING_DEPOSIT,
      then: (schema) => schema.required(i18n.t('errors.required')),
    }),
    selectedStartDate: yup.string().when(['transferType', 'depositType'], {
      is: (transferType: TransferType, depositType: DepositType) =>
        transferType === TransferType.DEPOSIT && depositType === DepositType.RECURRING_DEPOSIT,
      then: (schema) => schema.required(i18n.t('errors.required')),
      otherwise: (schema) => schema.optional(),
    }),
    // TODO: Remove availableWithdrawalAmount from form schema
    // This is currently used to avoid prop drilling but should be moved to a proper store (Zustand)
    // The form schema should only contain actual form fields that users interact with
    availableWithdrawalAmount: yup.string().default('0'),
    taxYear: yup.number().optional().default(undefined),
  })
  .test('account-validation', i18n.t('errors.transferErrors.accountValidationFailed'), function () {
    const ctx = this.options.context as TransferValidationContext;
    if (!ctx) return true;

    const { isAccountValid } = ctx;

    if (!isAccountValid) {
      return this.createError({
        message: i18n.t('errors.transferErrors.accountInvalid'),
        path: 'amount',
      });
    }

    return true;
  });

export type TransferFormData = yup.InferType<typeof transferSchema>;
